type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerOptions {
  /**
   * Debug flag name (e.g. 'API') that maps to `REACT_APP_DEBUG_${flag}`
   */
  flag?: string;
  /**
   * Allow logging in development even if no debug flags are enabled.
   * Defaults to `false` to keep the console quiet by default.
   */
  enabledInDev?: boolean;
}

interface BatchOptions<T = unknown> {
  /**
   * Log level to use when the batch flushes.
   */
  level?: LogLevel;
  /**
   * Milliseconds to wait before flushing the batch.
   */
  flushMs?: number;
  /**
   * Optional header text or formatter that produces header text.
   */
  header?: string | ((entries: T[]) => string);
  /**
   * Optional formatter that transforms the collected entries into a log payload.
   */
  formatter?: (entries: T[]) => unknown;
}

const isProduction = process.env.NODE_ENV === 'production';

const toBoolean = (value?: string): boolean => value?.toLowerCase() === 'true';

const getGlobalDebug = (): boolean => toBoolean(process.env.REACT_APP_DEBUG_MODE);

const normalizeFlag = (flag: string): string =>
  flag.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_');

const isFlagEnabled = (flag?: string): boolean => {
  if (!flag) {
    return false;
  }

  const normalized = normalizeFlag(flag);
  const envKey = `REACT_APP_DEBUG_${normalized}` as keyof NodeJS.ProcessEnv;
  return toBoolean(process.env[envKey]);
};

const consoleMap: Record<LogLevel, (...args: unknown[]) => void> = {
  debug: console.debug ?? console.log,
  info: console.info ?? console.log,
  warn: console.warn ?? console.log,
  error: console.error ?? console.log,
};

const batchedEntries = new Map<string, unknown[]>();
const batchedTimers = new Map<string, ReturnType<typeof setTimeout>>();

const buildBatchKey = (namespace: string, key: string): string =>
  `${namespace}:${key}`;

const shouldEmit = (options?: LoggerOptions): boolean => {
  if (isProduction) {
    return false;
  }

  const globalEnabled = getGlobalDebug();
  const flagEnabled = isFlagEnabled(options?.flag);
  const devOverride = !globalEnabled && !flagEnabled && options?.enabledInDev;

  return globalEnabled || flagEnabled || !!devOverride;
};

export interface Logger {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  /**
   * Emit a throttled log. Subsequent calls with the same key within the interval are ignored.
   */
  throttled: (
    key: string,
    intervalMs: number,
    level: LogLevel,
    ...args: unknown[]
  ) => void;
  /**
   * Collect entries and flush them as a single log after the configured delay.
   */
  batch: <T = unknown>(key: string, entry: T, options?: BatchOptions<T>) => void;
  /**
   * Returns whether logging is currently enabled for this logger.
   */
  isEnabled: () => boolean;
}

/**
 * Factory for environment-aware loggers used across the React app.
 * Logs are silent in production and opt-in in development via debug flags.
 */
export const createLogger = (
  namespace: string,
  options: LoggerOptions = {}
): Logger => {
  const logPrefix = namespace ? `[${namespace}]` : '';
  const throttleTracker = new Map<string, number>();

  const emit = (level: LogLevel, args: unknown[]): void => {
    if (!shouldEmit(options)) {
      return;
    }

    const writer = consoleMap[level];
    if (writer) {
      writer(logPrefix, ...args);
    }
  };

  const throttled = (
    key: string,
    intervalMs: number,
    level: LogLevel,
    ...args: unknown[]
  ): void => {
    if (!shouldEmit(options)) {
      return;
    }

    const lastRun = throttleTracker.get(key) ?? 0;
    const now = Date.now();

    if (now - lastRun < intervalMs) {
      return;
    }

    throttleTracker.set(key, now);
    emit(level, args);
  };

  const batch = <T,>(key: string, entry: T, batchOptions?: BatchOptions<T>): void => {
    if (!shouldEmit(options)) {
      return;
    }

    const { flushMs = 250, level = 'debug', header, formatter } = batchOptions ?? {};
    const batchKey = buildBatchKey(namespace, key);
    const entries = (batchedEntries.get(batchKey) ?? []) as T[];
    entries.push(entry);
    batchedEntries.set(batchKey, entries);

    if (batchedTimers.has(batchKey)) {
      return;
    }

    const timer = setTimeout(() => {
      batchedTimers.delete(batchKey);
      const buffered = (batchedEntries.get(batchKey) ?? []) as T[];
      batchedEntries.delete(batchKey);

      if (buffered.length === 0) {
        return;
      }

      const payload = formatter ? formatter(buffered) : buffered;
      const headerText =
        typeof header === 'function' ? header(buffered) : header;

      if (headerText) {
        emit(level, [headerText, payload]);
      } else {
        emit(level, [payload]);
      }
    }, flushMs);

    batchedTimers.set(batchKey, timer);
  };

  return {
    debug: (...args: unknown[]) => emit('debug', args),
    info: (...args: unknown[]) => emit('info', args),
    warn: (...args: unknown[]) => emit('warn', args),
    error: (...args: unknown[]) => emit('error', args),
    throttled,
    batch,
    isEnabled: () => shouldEmit(options),
  };
};

/**
 * Exposed helper for cases where consumers only need a boolean to guard inline logging.
 */
export const isDebugEnabled = (flag?: string, enabledInDev?: boolean): boolean =>
  shouldEmit({ flag, enabledInDev });
